import METHODS from "./methods";
import { combineReducers } from "redux";
import NoopView from "./common/noop";
import SuccessView from "./common/success";
import FailureView from "./common/failure";
import TimeoutView from "./common/timeout";
import RedirectView from "./common/redirect";
import Processing from "../common/processing";
import PlaceholderView from "./common/placeholder";
import ConfirmingView from "./common/confirming";
import PendingView from "./common/pending";

import {
  constant,
  callback as __callback__,
  isFunction,
  isPlainObject,
  getValueAtPath,
  withProperties,
  objectWithAccessors,
  isBoolean
} from "../../utils";

let $currentView, $successful;

const reducers = {};
const STATE_IDENTIFIER = 'view';
const PAYMENT_VIEWS = Object.create(null);
const PAYMENT_METHODS = Object.create(null);

function __initializePaymentMethod__(initializer) {
  if (isFunction(initializer) && isPlainObject(this)) {
    const { name, method, views, mount, unmount, $context: $rootContext } = initializer(this.$data, this.$store);

    withProperties(PAYMENT_METHODS, {
      [method]: Object.freeze(withProperties(Object.create(null), {
        name,
        mount: __callback__(mount),
        unmount: __callback__(unmount)
      }))
    });

    if (this.$paymentMethods.indexOf(method) >= 0) {
      [].concat(views).forEach(view => {
        if (isPlainObject(view)) {
          const { NAME } = view;
          withProperties(view, { $rootContext });
          NAME && withProperties(PAYMENT_VIEWS, { [NAME]: view });
        }
      });
    }
  }
}

function __augmentPaymentView__(view) {
  view = isPlainObject(view) ? view : PAYMENT_VIEWS[view];

  if (isPlainObject(view) && isPlainObject(this)) {
    const $this = this;
    const { NAME } = view;
    const { dispatch, getState } = this.$store;

    objectWithAccessors.call(view, {
      DATA: () => $this.$data,
      STATE: () => getValueAtPath(getState(), `${STATE_IDENTIFIER}[context]`),
      ACTIVE: () => {
        const state = getState();
        // const processing = getValueAtPath(state, Processing.NAME);
        const currentView = getValueAtPath(state, `${STATE_IDENTIFIER}[current]`);
        return /* processing !== true && */NAME === currentView;
      }
    });

    withProperties(view, {
      activate(callback) {
        // Replace the current view context(state) with that of the activated view
        if ($this.$store.refresh.view.call(view)) {
          ($currentView = view) && __callback__(callback)(dispatch, view.STATE);
        }
      }
    });
  }
}

function initialize(data, store) {
  const context = objectWithAccessors({
    $data: () => data,
    $store: () => store,
    $paymentMethods: () => getValueAtPath(data, 'payment_options') || []
  });

  [ PlaceholderView, NoopView, RedirectView, SuccessView, ConfirmingView, PendingView, FailureView, TimeoutView ]
    .forEach(view => {
      isFunction(view) && withProperties(view, {
        activate(callback) {
          let $view = view.call(this);
          
          if (isPlainObject($view) && $view.NAME) {
            const { NAME } = $view;

            if (!(NAME in PAYMENT_VIEWS)) {
              withProperties(PAYMENT_VIEWS, { [NAME]: view });
            }
          
            __augmentPaymentView__.call(context, $view);
          }

          if (isPlainObject($view) && isFunction($view.activate)) {
            if (view !== PlaceholderView) PlaceholderView.activate();
            $view.activate(callback);
            view === SuccessView && ($successful = true);
            isFunction($view.$mount) && $view.$mount();
          }
        }
      });
    });

  METHODS.forEach(__initializePaymentMethod__.bind(context));
  Object.keys(PAYMENT_VIEWS).forEach(__augmentPaymentView__.bind(context));

  if (isFunction(store.refresh)) {
    const EmptyObjectReducer = constant(Object.create(null));

    withProperties(store.refresh, {
      view() {
        const viewContext = isPlainObject(this) ? this : {};

        if (isBoolean(viewContext.ACTIVE) && !viewContext.ACTIVE) {
          $currentView && __callback__($currentView.Reducer.teardown)();

          let { NAME = null, Reducer, setup } = viewContext;

          if (!isFunction(Reducer) && isFunction(setup)) {
            ({ Reducer } = (setup(), viewContext));
          }

          reducers[STATE_IDENTIFIER] = combineReducers({
            current: constant(NAME),
            context: isFunction(Reducer) ? Reducer : EmptyObjectReducer
          });

          store.refresh();
          __callback__(getValueAtPath(viewContext.$rootContext, '$inputs.$refresh'))();

          return viewContext.ACTIVE;
        }

        return false;
      }
    });

    store.refresh.view();
    PlaceholderView && PlaceholderView.activate();
  }
}

export { initialize, reducers, PAYMENT_METHODS, $successful };
